import { useCallback, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { CalendarDays } from "lucide-react";
import { PeriodRangeCalendar, isoFromState } from "@/components/PeriodRangeCalendar";
import { useClientConfig } from "@/contexts/ClientConfigContext";
import { formatarDataLocal, getTodaySaoPaulo, parseIsoDateOnlyLocal } from "@/lib/dateUtils";

type DatePickerProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  "aria-label"?: string;
};

type CalendarPosition = { top: number; left: number; width: number };

export function DatePicker({
  id,
  value,
  onChange,
  className = "",
  min,
  max,
  disabled = false,
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const { config } = useClientConfig();
  const generatedId = useId();
  const calendarId = `date-picker-${generatedId}`;
  const anchorRef = useRef<HTMLButtonElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => isoFromState(value || getTodaySaoPaulo()));
  const [position, setPosition] = useState<CalendarPosition | null>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const calendar = calendarRef.current;
    if (!anchor || !calendar) return;
    const rect = anchor.getBoundingClientRect();
    const margin = 8;
    const gap = 8;
    const width = Math.min(328, window.innerWidth - margin * 2);
    const height = calendar.getBoundingClientRect().height;
    const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));
    const below = window.innerHeight - rect.bottom - gap - margin;
    const above = rect.top - gap - margin;
    const top = below >= height || below >= above
      ? Math.min(rect.bottom + gap, window.innerHeight - height - margin)
      : Math.max(margin, rect.top - height - gap);
    setPosition({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!anchorRef.current?.contains(target) && !calendarRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        anchorRef.current?.focus();
      }
    };
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, updatePosition]);

  const openCalendar = () => {
    if (disabled) return;
    setViewMonth(isoFromState(value || getTodaySaoPaulo()));
    setPosition(null);
    setOpen(true);
  };

  const handleSelect = (iso: string) => {
    onChange(iso);
    setOpen(false);
    requestAnimationFrame(() => anchorRef.current?.focus());
  };

  const buttonLabel = ariaLabel ?? "Data";
  const calendarStyle: CSSProperties = {
    position: "fixed",
    top: position?.top ?? -10000,
    left: position?.left ?? -10000,
    width: position?.width ?? 328,
    maxWidth: "calc(100vw - 16px)",
    maxHeight: "calc(100dvh - 16px)",
    overflowY: "auto",
    zIndex: 1000,
    visibility: position ? "visible" : "hidden",
  };

  return (
    <>
      <button
        ref={anchorRef}
        id={id}
        type="button"
        className={`gs-modal-form-control box-border flex h-10 min-w-0 max-w-full w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-left text-sm text-foreground focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        aria-label={buttonLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={calendarId}
        disabled={disabled}
        onClick={() => open ? setOpen(false) : openCalendar()}
      >
        <span className={value ? "" : "text-muted-foreground"}>
          {value ? formatarDataLocal(parseIsoDateOnlyLocal(value)) : "DD/MM/AAAA"}
        </span>
        <CalendarDays className="h-4 w-4 shrink-0 text-foreground" aria-hidden="true" />
      </button>
      {open && typeof document !== "undefined" ? createPortal(
        <div
          id={calendarId}
          ref={calendarRef}
          role="dialog"
          aria-label={`Selecionar ${buttonLabel.toLocaleLowerCase("pt-BR")}`}
          style={calendarStyle}
        >
          <PeriodRangeCalendar
            viewMonth={viewMonth}
            onViewMonthChange={setViewMonth}
            selectedIso={value}
            onSelect={handleSelect}
            primaryColor={config.primary_color}
            ariaLabel={`Calendário de ${buttonLabel.toLocaleLowerCase("pt-BR")}`}
            compact
            minIso={min}
            maxIso={max}
          />
        </div>,
        document.body,
      ) : null}
    </>
  );
}
